import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useExportDocx } from './use-export-docx';
import { useExportExcel } from './use-export-excel';
import { useExportPdf } from './use-export-pdf';

import type { Meeting, ParsedTranscriptEntry } from '../utils/export-types';
import type { MeetingSummary, ActionItem, MeetingAgreement } from '@consigli/types';

interface MeetingExportData {
  summary: MeetingSummary | null;
  actionItems: ActionItem[];
  agreements: MeetingAgreement[];
}

interface UseMeetingExportsProps {
  meeting: Meeting;
  parsedTranscripts: ParsedTranscriptEntry[];
  // Shared, de-duplicated fetch from useMeetingSummaryData. This is the SAME
  // promise the on-open prefetch uses, so an export reuses any in-flight
  // generation instead of starting its own (which caused the ~1 min latency).
  fetchAllData: () => Promise<MeetingExportData>;
}

export const useMeetingExports = ({
  meeting,
  parsedTranscripts,
  fetchAllData,
}: UseMeetingExportsProps) => {
  const { t } = useTranslation();

  // Export hooks
  const { exportToPdf } = useExportPdf();
  const { exportToDocx } = useExportDocx();
  const { exportToExcel } = useExportExcel();

  // Export loading states
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  // Export handlers
  const handleExportPdf = useCallback(async () => {
    setIsExportingPdf(true);
    try {
      const freshData = await fetchAllData();
      await exportToPdf({
        meeting,
        transcripts: parsedTranscripts,
        summary: freshData.summary,
        actionItems: freshData.actionItems,
        agreements: freshData.agreements,
      });
      toast.success(
        t('meeting-secretary.meetings.export-success', 'Export completed successfully'),
      );
    } catch (error) {
      console.error('PDF export error:', error);
      if (error instanceof Error) {
        toast.error(
          t('meeting-secretary.meetings.export-error', 'Export failed: ') + error.message,
        );
      } else {
        toast.error(
          t('meeting-secretary.meetings.export-error-generic', 'An unexpected error occurred'),
        );
      }
    } finally {
      setIsExportingPdf(false);
    }
  }, [meeting, parsedTranscripts, exportToPdf, fetchAllData, t]);

  const handleExportDocx = useCallback(async () => {
    setIsExportingDocx(true);
    try {
      const freshData = await fetchAllData();
      await exportToDocx({
        meeting,
        transcripts: parsedTranscripts,
        summary: freshData.summary,
        actionItems: freshData.actionItems,
        agreements: freshData.agreements,
      });
      toast.success(
        t('meeting-secretary.meetings.export-success', 'Export completed successfully'),
      );
    } catch (error) {
      console.error('Export error:', error);
      if (error instanceof Error) {
        toast.error(
          t('meeting-secretary.meetings.export-error', 'Export failed: ') + error.message,
        );
      } else {
        toast.error(
          t('meeting-secretary.meetings.export-error-generic', 'An unexpected error occurred'),
        );
      }
    } finally {
      setIsExportingDocx(false);
    }
  }, [meeting, parsedTranscripts, exportToDocx, fetchAllData, t]);

  const handleExportExcel = useCallback(async () => {
    setIsExportingExcel(true);
    try {
      const freshData = await fetchAllData();
      await exportToExcel({
        meeting,
        transcripts: parsedTranscripts,
        summary: freshData.summary,
        actionItems: freshData.actionItems,
        agreements: freshData.agreements,
      });
      toast.success(
        t('meeting-secretary.meetings.export-success', 'Export completed successfully'),
      );
    } catch (error: unknown) {
      console.error('Excel export error:', error);
      if (error instanceof Error) {
        toast.error(
          t('meeting-secretary.meetings.export-error', 'Export failed: ') + error.message,
        );
      } else {
        toast.error(
          t('meeting-secretary.meetings.export-error-generic', 'An unexpected error occurred'),
        );
      }
    } finally {
      setIsExportingExcel(false);
    }
  }, [meeting, parsedTranscripts, exportToExcel, t, fetchAllData]);

  return {
    // Export handlers
    handleExportPdf,
    handleExportDocx,
    handleExportExcel,
    // Loading states
    isExportingPdf,
    isExportingDocx,
    isExportingExcel,
    isExporting: isExportingPdf || isExportingDocx || isExportingExcel,
  };
};
